import { MatDialog } from '@angular/material/dialog';
import { CookieService } from 'ngx-cookie-service';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { of, Observable, throwError } from 'rxjs';
import { catchError, mapTo, tap } from 'rxjs/operators';
import { AppConfigService } from '../app-config.service';
import { CommonService } from '../common.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private config: any;
  private readonly ACCESS_TOKEN = 'ACCESS_TOKEN';
  private readonly REFRESH_TOKEN = 'REFRESH_TOKEN';
  private readonly SESSION_ID = 'SESSION_ID';
  private readonly VIEWER_SLIDES = 'VIEWER_SLIDES';
  private readonly URL_PARENT = 'URL_PARENT';
  private readonly MRN_ID = 'MRN_ID';
  private readonly MRN_STATUS = 'MRN_STATUS';
  private readonly MRN_SPECIMEN = 'MRN_SPECIMEN';
  private readonly PARENT_PROJECT = 'PARENT_PROJECT';
  private readonly LAST_PARENT_APP_URL = 'LAST_PARENT_APP_URL';
  private readonly VIEWER_BASE_NAME = 'VIEWER_BASE_NAME';
  private readonly VIEWER_OPEN_TYPE = 'VIEWER_OPEN_TYPE';
  private readonly CURR_USER = 'CURR_USER';
  private readonly MRN_ORGAN = 'MRN_ORGAN';
  // private readonly FETCH_VIEWER_DATA_FROM = 'FETCH_VIEWER_DATA_FROM';

  private loggedUser: string = '';
  isLoginPopupOpened:boolean = false; 
  allAPIRequestArr:any = [];

  constructor(private http: HttpClient,
    private cookieService: CookieService,
    private commonService: CommonService,
    private configService: AppConfigService,
    private dialog: MatDialog,) {
    this.config = this.configService.config;
  }

  login(user: { email: string, password: string }): Observable<string> {
    return this.http.post<any>(`${this.config.apiUrl}user/login`, user/*dataObj*/)
      .pipe(
        tap(tokens => {
          this.storeTokens(tokens);
          // this.storeLoginData(tokens);
        }),
        mapTo("true"),
        catchError(error => {
          if (error.error.status == 403 && error.error.message == 'USER_ALREADY_ACTIVE') {
            return of('discardOldSession');
          } else {
            return of(error.error.status == 401 ? "invalid" : "valid");
          }
        }));
  }

  checkToken() {
    return this.http.get(`${this.config.apiUrl}user/isTokenAlive`);
  }

  getUserInfo() {
    return this.http.get(`${this.config.apiUrl}user/userinfo`);
  }

  getUserRoles() {
    return this.http.get(`${this.config.apiUrl}user/roles`);
  }

  getRolePlaces(role: any) {
    return this.http.get(`${this.config.apiUrl}user/roles/${role}/places`);
  }

  logout() {
    let logoutData = {
      "refreshToken": this.getRefreshToken(),
      "sessionId": this.getSessionId()
    }
    return this.http.post<any>(`${this.config.apiUrl}user/logout`, logoutData).pipe(
      tap(() => {
        this.doLogoutUser()
      }),
      mapTo(true),
      catchError(error => {
        this.doLogoutUser()
        //alert(error.error);
        return of(false);
      }));
  }

  logoutAPICall() {
    let logoutData = {
      "refreshToken": this.getRefreshToken(),
      "sessionId": this.getSessionId()
    }
    return this.http.post<any>(`${this.config.apiUrl}user/logout`, logoutData)
  }

  isLoggedIn() {
    return !!this.getAccessToken();
  }

  refreshToken() {
    this.storeAccessToken('');
    const requestData = {
      'refreshToken': this.getRefreshToken(),
      'sessionId': this.getSessionId()
    }
    return this.http.post(`${this.config.apiUrl}user/token/refresh`, requestData)
      .pipe(tap((tokens: any) => {
        this.storeTokens(tokens);
      }));
  }

  getAccessToken() {
    if (this.config.fetchViewerDataFrom != "iFrame") {
      return this.cookieService.get(this.ACCESS_TOKEN);
    } else {
      return this.commonService.ProjectLSData['accessToken']
    }
  }

  getSessionId() {
    if (this.config.fetchViewerDataFrom != "iFrame") {
      return this.cookieService.get(this.SESSION_ID);
    } else {
      return this.commonService.ProjectLSData['sessionId'];
    }
  }

  doLogoutUser() {
    this.removeTokens();
    this.clearLocalStorage();
    this.closeAllOpenDialog();
  }

  closeAllOpenDialog() {
    if (this.dialog.openDialogs.length > 0) {
      this.dialog.closeAll();
      return false;
    }
    return true;
  }

  getRefreshToken() {
    if (this.config.fetchViewerDataFrom != "iFrame") {
      return this.cookieService.get(this.REFRESH_TOKEN);
    } else {
      return this.commonService.ProjectLSData['refreshToken'];
    }
  }

  getMRNStatus() {
    return this.cookieService.get(this.MRN_STATUS);
  }

  private storeAccessToken(access: string) {
    if (this.config.fetchViewerDataFrom != "iFrame") {
      this.cookieService.set(this.ACCESS_TOKEN, access, 0, '/');
    } else {
      this.commonService.ProjectLSData['accessToken'] = access;
      this.commonService.setProjectLSData();
    }
  }

  public storeViewerSlides(slidesArr: string) {
    this.cookieService.set(this.VIEWER_SLIDES, slidesArr, 0, '/');
  }

  /* public storeViewerDataFrom(viewerDataFrom: string) {
    this.cookieService.set(this.FETCH_VIEWER_DATA_FROM, viewerDataFrom, 0, '/');
  } */

  public storeMRNID(mrnId: any) {
    this.cookieService.set(this.MRN_ID, mrnId, 0, '/');
  }

  public CurrentUser(currUser: any) {
    this.cookieService.set(this.CURR_USER, currUser, 0, '/');
  }

  public storeMRNStatus(mrnStatus: any) {
    this.cookieService.set(this.MRN_STATUS, mrnStatus, 0, '/');
  }

  public storeMRNOrgan(mrnOrgan: any) {
    this.cookieService.set(this.MRN_ORGAN, mrnOrgan, 0, '/');
  }

  public storeMRNSpecimen(mrnStatus: any) {
    this.cookieService.set(this.MRN_SPECIMEN, mrnStatus, 0, '/');
  }

  public storeParentProject(ParentProject: any) {
    this.cookieService.set(this.PARENT_PROJECT, ParentProject, 0, '/');
  }

  public storeLastParentAppUrl(parentAppUrl: any) {
    this.cookieService.set(this.LAST_PARENT_APP_URL, parentAppUrl, 0, '/');
  }

  public storeUrlApi(urlParent: any) {
    this.cookieService.set(this.URL_PARENT, urlParent, 0, '/');
  }

  public storeWebViewerBaseName(baseName: any) {
    this.cookieService.set(this.VIEWER_BASE_NAME, baseName, 0, '/');
  }

  public storeWebViewerOpenType(openType: any) {
    this.cookieService.set(this.VIEWER_OPEN_TYPE, openType, 0, '/');
  }

  storeTokens(tokens: any) {
    if(tokens.data){
      tokens = tokens.data;
    }
    if (this.config.fetchViewerDataFrom != "iFrame") {
      this.cookieService.set(this.ACCESS_TOKEN, tokens.accessToken, 0, '/');
      this.cookieService.set(this.REFRESH_TOKEN, tokens.refreshToken, 0, '/');
      this.cookieService.set(this.SESSION_ID, tokens.sessionId, 0, '/');
    } else {
      this.commonService.ProjectLSData['accessToken'] = tokens.accessToken;
      this.commonService.ProjectLSData['refreshToken'] = tokens.refreshToken;
      this.commonService.ProjectLSData['sessionId'] = tokens.sessionId;
      this.commonService.setProjectLSData();
    }
  }

  private storeLoginData(resp: any) {
    localStorage['isfirstLogin'] = resp.isFirstTimeLogin ? resp.isFirstTimeLogin : false;
    localStorage['isSessionReplaced'] = resp.sessionReplaced ? resp.sessionReplaced : false;
    localStorage['chooseSession'] = resp.chooseSession ? resp.chooseSession : false;
  }

  removeTokens() {
    // return;
    this.cookieService.delete(this.ACCESS_TOKEN, '/');
    this.cookieService.delete(this.REFRESH_TOKEN, '/');
    this.cookieService.delete(this.SESSION_ID, '/');
    this.cookieService.delete(this.VIEWER_SLIDES, '/');
    this.cookieService.delete(this.URL_PARENT, '/');
    this.cookieService.delete(this.MRN_ID, '/');
    this.cookieService.delete(this.MRN_STATUS, '/');
    this.cookieService.delete(this.PARENT_PROJECT, '/');
    this.cookieService.delete(this.LAST_PARENT_APP_URL, '/');
    this.cookieService.delete(this.MRN_ORGAN, '/');
    this.cookieService.delete(this.CURR_USER, '/');
    this.cookieService.delete(this.VIEWER_OPEN_TYPE, '/');
    this.cookieService.delete(this.VIEWER_BASE_NAME, '/');
  }

  clearLocalStorage() {
    // localStorage.clear();
    this.commonService.ProjectLSData = {};
    if (this.commonService?.projectName) {
      delete localStorage[this.commonService.projectName];
    }
  }

  activateSession() {
    return this.http.post(`${this.config.apiUrl}user/session/activate`, { sessionId: this.getSessionId() });
  }
}
